import { SupportedLanguages } from '@core/_types/sharedTypes';

export default class EnumHelper<T> {
	enumList: ToListItems<T>[];

	constructor(toListItems: ToListItems<T>[]) {
		this.enumList = toListItems;
	}

	getDisplay = (item: T, language: SupportedLanguages = 'English'): string => {
		const listItem = this.enumList.find(f => f.value === item) as ToListItems<T> | undefined;
		return listItem?.localizedDisplay?.[language] ?? listItem?.display ?? '';
	};

	getToListItemDisplay = (toListItem: ToListItems<T>, language: SupportedLanguages = 'English'): string => getToListItemDisplay(toListItem, language);

	getToListItemValue = (toListItem: ToListItems<T>) => getToListItemValue(toListItem);
}

export interface ToListItems<T> {
	display: string;
	value: T;
	[key: string]: any;
	localizedDisplay?: { [key in SupportedLanguages]?: string };
}

export type EnumToList<T extends string | number | symbol> = {
	[K in T]: ToListItems<T>;
};

export function getToListItemDisplay<T>(toListItem: ToListItems<T>, language: SupportedLanguages = 'English'): string {
	return toListItem.localizedDisplay?.[language] ?? toListItem.display ?? '';
}

export function getToListItemValue<T>(toListItem: ToListItems<T>): T {
	return toListItem.value;
}

/** Contains helper methods for performing basic operations on bit flags */
export abstract class BitFlagHelper {
	static addFlag(baseFlag: number, valueToAdd: number): number {
		return baseFlag | valueToAdd;
	}

	static removeFlag(baseFlag: number, valueToRemove: number): number {
		return baseFlag & ~valueToRemove;
	}

	/** Returns true if all flags enabled in the `valueToCheck` are enabled in the `baseFlag`. */
	static contains(baseFlag: number, valueToCheck: number): boolean {
		return (baseFlag & valueToCheck) === valueToCheck;
	}

	/** Returns true if any of the flags enabled in the `valueToCheck` are enabled in the `baseFlag` */
	static containsSome(baseFlag: number, valueToCheck: number): boolean {
		return (baseFlag & valueToCheck) > 0;
	}

	/** Given two flag values, returns values that are enabled in both flags. */
	static getIntersection(flagA: number, flagB: number): number {
		return flagA & flagB;
	}

	/** Returns true if the two flag values share any values in common. */
	static hasIntersection(flagA: number, flagB: number): boolean {
		return this.getIntersection(flagA, flagB) !== 0;
	}

	/** Takes a flag value and splits it into a list of all enabled flag enum values. */
	static toArray<E extends { [key: number]: number | string }>(enumType: E, flagValue: E[keyof E]): number[] {
		if (typeof flagValue !== 'number') throw new Error(`Unable to convert this flag value. Received: ${flagValue} (type ${typeof flagValue})`);

		const maxValue = this._getMaxEnumValue(enumType);

		const array = [];
		for (let i = 1; i <= maxValue; i = i << 1) {
			if ((flagValue & i) !== 0) array.push(i);
		}

		return array;
	}

	/** Similar to the `toArray()` method, but returns a list of the flag's string keys instead of its values. */
	static toKeyArray<E extends { [key: number]: number | string }>(enumType: E, flagValue: E[keyof E]): string[] {
		const valueList = this.toArray(enumType, flagValue);
		return valueList.map(v => enumType[v].toString());
	}

	/** Takes a list of individual enum flag values and combines them together in a bitwise OR operation. */
	static toFlag(flagValues: number[]): number {
		let flag = 0;
		flagValues.forEach(f => (flag |= f));
		return flag;
	}

	private static _getMaxEnumValue<E extends { [key: number]: number | string }>(enumType: E): number {
		let maxValue = 0;
		Object.values(enumType).forEach(v => {
			const vNum = Number(v);
			if (!Number.isNaN(vNum) && vNum > maxValue) maxValue = vNum;
		});

		return maxValue;
	}
}
